/**
 * ServiceDueWidget
 * Visar antal förfallande servicepunkter på dashboard.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import { WrenchScrewdriverIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { serviceApi, ServiceOverviewCount } from '@/services/service/serviceApi';
import { useNavigate } from 'react-router-dom';

const ServiceDueWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<ServiceOverviewCount | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await serviceApi.getServiceOverviewCount();
      setCounts(data);
    } catch (err: unknown) {
      // 404 = service-modulen är inte deployad/konfigurerad – visa tyst som "ingen service"
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setCounts({ total: 0, overdue: 0, warning: 0, booked: 0 });
      } else {
        console.error('Failed to load service counts:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const title = t('widgets.serviceDue');
  const total = counts?.total || 0;
  const hasOverdue = (counts?.overdue || 0) > 0;

  return (
    <div className="bg-bgCard border border-borderDefault rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-borderDefault">
        <div className="flex items-center gap-2">
          <WrenchScrewdriverIcon className={`w-5 h-5 ${hasOverdue ? 'text-red-500' : 'text-yellow-500'}`} />
          <h3 className="text-sm font-semibold text-textDefault">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {hasOverdue && (
            <span className="flex items-center gap-1 bg-red-100 dark:bg-red-900/30 text-red-600 text-xs font-medium px-2 py-0.5 rounded-full">
              <ExclamationTriangleIcon className="w-3 h-3" />
              {counts?.overdue} {t('widgets.overdue')}
            </span>
          )}
          <button onClick={load} className="p-1 hover:bg-bgHover rounded">
            <ArrowPathIcon className={`w-4 h-4 text-textTertiary ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div
        className="p-4 cursor-pointer hover:bg-bgHover transition-colors"
        onClick={() => navigate('/service')}
      >
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin h-5 w-5 border-2 border-yellow-500 border-t-transparent rounded-full" />
          </div>
        ) : total === 0 ? (
          <div className="text-center py-3 text-sm text-textTertiary">
            {t('widgets.noServiceDue')}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-center">
              <div className="text-3xl font-bold text-textDefault">{total}</div>
              <div className="text-xs text-textTertiary mt-1">
                {t('widgets.servicePointsNeedAction')}
              </div>
              <div className="text-[11px] text-textTertiary/80 mt-1 max-w-[14rem] mx-auto leading-snug">
                {t('service.horizonHint')}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {(counts?.overdue || 0) > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
                  <div className="text-lg font-bold text-red-600">{counts?.overdue}</div>
                  <div className="text-xs text-red-500">{t('widgets.overdueLabel')}</div>
                </div>
              )}
              {(counts?.warning || 0) > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-2">
                  <div className="text-lg font-bold text-yellow-600">{counts?.warning}</div>
                  <div className="text-xs text-yellow-500">{t('widgets.soonLabel')}</div>
                </div>
              )}
              {(counts?.booked || 0) > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                  <div className="text-lg font-bold text-blue-600">{counts?.booked}</div>
                  <div className="text-xs text-blue-500">{t('widgets.bookedLabel')}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDueWidget;
